if (!customElements.get('vip-membership')) {
  customElements.define(
    'vip-membership',
    class VipMembership extends HTMLElement {
      connectedCallback() {
        // placeBelowKaching() moves this element, which re-runs connectedCallback.
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.section;
        this.form = document.getElementById(this.dataset.formId);
        this.isExistingMember = this.dataset.existingMember === 'true';
        this.membershipVariantId = Number(this.dataset.membershipVariant);
        this.sellingPlanId = Number(this.dataset.sellingPlan);
        this.discount = Number(this.dataset.discount) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.variantPrices = JSON.parse(this.querySelector('[data-variant-prices]').textContent);
        // Only the theme's own Add to cart / Buy now buttons get hidden while VIP is
        // selected. Kaching puts its widget inside the same buy-buttons block, so
        // hiding the whole block would hide the bundles (and this element) too.
        this.themeButtons = this.form
          ? [...this.form.querySelectorAll('.product-form__submit, .shopify-payment-button')]
          : [];
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.errorEl = this.querySelector('[data-vip-error]');
        this.vipButton = this.querySelector('[data-vip-submit]');
        this.kachingPrice = null;
        // Toggle layout: the Member / Regular switch sits above the Kaching bundles
        // (placeBelowKaching() moves it there) and each bundle shows member or
        // regular pricing.
        this.toggleEl = this.dataset.layout === 'toggle' ? this.querySelector('[data-vip-toggle]') : null;
        this.threshold = Number(this.dataset.threshold) || 10000;
        // Member deal (logged-in members only): cents off one item, 0 when there is no deal.
        this.dealAmount = Number(this.dataset.dealAmount) || 0;

        if (!this.form) return;

        this.querySelectorAll('.vip-membership__radio').forEach((radio) =>
          radio.addEventListener('change', () => this.update())
        );
        this.vipButton?.addEventListener('click', () => this.submit());

        this.terms = this.querySelector('[data-vip-terms]');

        // Toggle layout: "Join the VIP Club" checkbox above Add to cart while Regular
        // is picked. Ticking it picks Member Price, which hides the row again.
        this.clubRow = this.toggleEl ? this.querySelector('[data-vip-club]') : null;
        if (this.clubRow) {
          const box = this.clubRow.querySelector('[data-vip-club-box]');
          box.addEventListener('change', () => {
            if (!box.checked) return;
            const memberRadio = this.toggleEl.querySelector('.vip-membership__radio[value="member"]');
            if (memberRadio) memberRadio.checked = true;
            this.update();
          });
          // The terms link sits inside the checkbox label: open the terms without ticking the box.
          this.clubRow.querySelector('[data-vip-club-terms]')?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.terms?.showModal();
          });
        }
        if (this.terms) {
          this.terms.querySelector('[data-vip-terms-close]')?.addEventListener('click', () => this.terms.close());
          // Close when tapping the dimmed backdrop around the popup.
          this.terms.addEventListener('click', (event) => {
            if (event.target === this.terms) this.terms.close();
          });
        }

        // The theme's sticky Add to cart bar would otherwise add the bundle
        // without the membership while VIP is selected.
        const stickyAtc = document.getElementById(`sticky-atc-${this.sectionId}`);
        if (stickyAtc && !stickyAtc.dataset.scrollBtn) {
          stickyAtc.addEventListener(
            'click',
            (event) => {
              if (this.isExistingMember || !this.isMember || !event.target.closest('.sticky-atc__button')) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.submit();
            },
            true
          );
        }

        // Keep the sticky bar hidden until the VIP options have scrolled off the top of
        // the screen. While VIP is selected the theme's own Add to cart is hidden, so the
        // theme would otherwise show the bar straight away.
        this.stickyAtc = stickyAtc;
        if (stickyAtc && 'IntersectionObserver' in window) {
          stickyAtc.classList.add('vip-sticky-held');
          new IntersectionObserver(([entry]) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyAtc.classList.toggle('vip-sticky-held', !scrolledPast);
          }).observe(this);
        }

        // Kaching Bundles reports the selected deal's total price whenever the
        // shopper changes deal, variant or upsells.
        this.onKachingChange = (event) => {
          if (!this.isOwnKachingBlock(event.target)) return;
          const amount = event.detail?.pricing?.discountedPrice?.amount;
          if (typeof amount === 'number') this.kachingPrice = this.toCents(amount);
          this.placeBelowKaching();
          this.update();
        };
        document.addEventListener('variants-changed', this.onKachingChange);
        this.onKachingReady = () => {
          this.placeBelowKaching();
          this.update();
        };
        window.addEventListener('kaching-bundles-initialized', this.onKachingReady);
        document.addEventListener('kaching-bundles-block-loaded', this.onKachingReady);
        this.placeBelowKaching();

        // The theme's own bundle and variant pickers re-render on change; recalculate after them.
        this.onSectionChange = () => setTimeout(() => this.update(), 50);
        const section = this.closest('.shopify-section') || document;
        section.addEventListener('change', this.onSectionChange);
        section.addEventListener('click', this.onSectionChange);

        this.update();
      }

      get isMember() {
        return (this.toggleEl || this).querySelector('.vip-membership__radio:checked')?.value === 'member';
      }

      get kachingBlock() {
        const section = this.closest('.shopify-section') || document;
        return section.querySelector('kaching-bundles-block');
      }

      isOwnKachingBlock(target) {
        const block = this.kachingBlock;
        return block ? target === block || block.contains(target) : true;
      }

      // Kaching's widget can be placed by its app embed anywhere in the form area.
      // Keep the VIP options directly underneath it, above the Add to cart button.
      placeBelowKaching() {
        const block = this.kachingBlock;
        if (!block) return;
        const anchor = block.closest('.shopify-app-block, .kaching-bundles__block') || block;
        if (!anchor.contains(this) && anchor.nextElementSibling !== this) anchor.after(this);
        if (this.toggleEl && anchor.previousElementSibling !== this.toggleEl) anchor.before(this.toggleEl);
        this.observeKaching();
      }

      // Perks and trust line sit under the theme's Add to cart buttons, so they show
      // below whichever button (member or regular) is visible.
      placePerks() {
        const perks = this.perksEl ?? (this.perksEl = this.querySelector('[data-vip-perks]'));
        const buttons = this.submitButton?.closest('.product-form__buttons') || this.form?.querySelector('.product-form__buttons');
        // Toggle layout: the benefits go under the featured review, so the review
        // stays close to the Add to cart button.
        const review = this.dataset.layout === 'toggle' ? this.closest('.product__info-container')?.querySelector('.review-items-container') : null;
        const anchor = review || buttons;
        if (!perks || !anchor || anchor.nextElementSibling === perks) return;
        perks.classList.toggle('vip-perks-wrap--after-review', !!review);
        anchor.after(perks);
      }

      // Kaching re-renders its bundles on every change, so the member pricing is
      // written again whenever its markup changes. If Kaching's markup ever stops
      // matching, its own prices simply show (see vip-membership.css).
      observeKaching() {
        const block = this.kachingBlock;
        if ((!this.toggleEl && !this.dealAmount) || !block || this.observedBlock === block) return;
        this.observedBlock = block;
        new MutationObserver(() => this.scheduleBundlePricing()).observe(block, {
          childList: true,
          subtree: true,
          characterData: true,
        });
        ['deal-bar-selected', 'items-changed'].forEach((type) => block.addEventListener(type, () => this.update()));
      }

      scheduleBundlePricing() {
        if (this.bundlePricingPending) return;
        this.bundlePricingPending = true;
        requestAnimationFrame(() => {
          this.bundlePricingPending = false;
          this.renderBundlePricing();
          this.renderNote();
          this.renderDealBarTag();
        });
      }

      unitPrice() {
        const variantId = this.form.querySelector('[name="id"]')?.value;
        return this.variantPrices[variantId] ?? Object.values(this.variantPrices)[0];
      }

      // The member deal is for one item: it only shows while a single item is selected.
      dealPriceOf(regular) {
        if (!this.dealAmount) return null;
        const full = this.getFullPrice() ?? regular;
        if (full > this.unitPrice()) return null;
        return Math.max(0, regular - this.dealAmount);
      }

      // Writes *word* as gold, bold text.
      setHighlighted(element, text) {
        if (!element || element.dataset.text === text) return;
        element.dataset.text = text;
        element.replaceChildren(
          ...text.split(/\*([^*]+)\*/).map((part, index) => {
            if (index % 2 === 0) return document.createTextNode(part);
            const highlight = document.createElement('strong');
            highlight.className = 'vip-toggle__highlight';
            highlight.textContent = part;
            return highlight;
          })
        );
      }

      // Member card: the deal price while one item is selected, otherwise the usual
      // member price with a line pointing to the 1-item deal.
      renderDeal(regular, deal) {
        if (!this.dealAmount) return;
        const unit = this.unitPrice();
        const dealUnitText = this.formatMoney(Math.max(0, unit - this.dealAmount));
        const tag = this.querySelector('[data-vip-deal-tag]');
        if (tag) tag.hidden = deal === null;
        const title = this.querySelector('[data-vip-title]');
        const subtitle = this.querySelector('[data-vip-subtitle]');
        if (!this.memberCopy) this.memberCopy = { title: title?.textContent, subtitle: subtitle?.textContent };
        if (deal !== null) {
          const percent = `${Math.round((this.dealAmount * 100) / unit)}%`;
          if (title) title.textContent = this.dataset.dealTitle || this.memberCopy.title;
          this.setHighlighted(subtitle, (this.dataset.dealSubtitle || '').replace('[percent]', percent));
        } else {
          if (title) title.textContent = this.memberCopy.title;
          this.setHighlighted(subtitle, this.memberCopy.subtitle);
        }
        const hint = this.querySelector('[data-vip-deal-hint]');
        if (hint) {
          hint.hidden = deal !== null;
          hint.textContent = hint.dataset.template.replace('[price]', dealUnitText);
        }
        this.renderDealBarTag();
      }

      // A small tag on the 1-item bundle while another bundle is selected.
      renderDealBarTag() {
        const block = this.kachingBlock;
        if (!this.dealAmount || !block || !this.dataset.dealBarTag) return;
        const unit = this.unitPrice();
        const text = this.dataset.dealBarTag.replace('[price]', this.formatMoney(Math.max(0, unit - this.dealAmount)));
        block.querySelectorAll('.kaching-bundles__bar').forEach((bar) => {
          const data = this.readBar(bar);
          const line = bar.querySelector('.kaching-bundles__bar-first-line');
          let tag = line?.querySelector(':scope > .vip-deal-bar-tag');
          const show = data && data.full <= unit && !bar.classList.contains('kaching-bundles__bar--selected');
          if (!show || !line) {
            tag?.remove();
            return;
          }
          if (tag?.textContent === text) return;
          if (!tag) {
            tag = document.createElement('span');
            tag.className = 'vip-deal-bar-tag';
            line.append(tag);
          }
          tag.textContent = text;
        });
      }

      memberPriceOf(regular) {
        // Shopify rounds the percentage discount amount down to the cent.
        return regular - Math.floor((regular * this.discount) / 100);
      }

      parseMoney(text) {
        const cleaned = (text || '').replace(/[^\d.,]/g, '');
        if (!cleaned) return null;
        // "1.234,56" or "34,99" use a decimal comma; "1,234.56" a decimal point.
        const decimalComma = /,\d{2}$/.test(cleaned);
        const normalized = decimalComma ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '');
        const value = Number.parseFloat(normalized);
        return Number.isNaN(value) ? null : Math.round(value * 100);
      }

      // Reads a Kaching bundle's price and its full (crossed-out) price from Kaching's own markup.
      readBar(bar) {
        const pricing = bar?.querySelector('.kaching-bundles__bar-pricing');
        const price = this.parseMoney(pricing?.querySelector('.kaching-bundles__bar-price')?.textContent);
        if (!pricing || price === null) return null;
        const full = this.parseMoney(pricing.querySelector('.kaching-bundles__bar-full-price')?.textContent);
        return { pricing, price, full: Math.max(price, full || 0) };
      }

      // "You save 10%" while the full value is under the threshold, "You save $41.98" from it up.
      savingsText(full, shown) {
        if (full <= shown) return '';
        if (full < this.threshold) {
          const percent = Math.round(((full - shown) * 100) / full);
          return this.dataset.savePercent.replace('[percent]', `${percent}%`);
        }
        return this.dataset.saveAmount.replace('[savings]', this.formatMoney(full - shown));
      }

      renderBundlePricing() {
        const block = this.kachingBlock;
        if (!this.toggleEl || !block) return;
        const showMember = this.isMember;

        block.querySelectorAll('.kaching-bundles__bar').forEach((bar) => {
          const data = this.readBar(bar);
          if (!data) return;
          const shown = showMember ? this.memberPriceOf(data.price) : data.price;
          this.renderBundleEach(bar, data, shown);
          // Member Price on: a tag with the extra member saving replaces "You save"
          // (a percentage under the dollar threshold, the amount from it up).
          const pillTemplate = showMember
            ? shown < this.threshold
              ? this.dataset.bundlePillPercent
              : this.dataset.bundlePillAmount
            : '';
          const savings = pillTemplate
            ? pillTemplate.replace('[savings]', this.formatMoney(data.price - shown))
            : this.savingsText(data.full, shown);
          const key = `${shown}|${data.full}|${savings}`;

          let element = data.pricing.querySelector(':scope > .vip-bar-pricing');
          if (element?.dataset.key === key) return;
          if (!element) {
            element = document.createElement('div');
            element.className = 'vip-bar-pricing';
            data.pricing.append(element);
          }
          element.dataset.key = key;

          const price = document.createElement('span');
          price.className = 'vip-bar-pricing__price';
          price.textContent = this.formatMoney(shown);
          const nodes = [price];
          if (data.full > shown) {
            const full = document.createElement('s');
            full.className = 'vip-bar-pricing__full';
            full.textContent = this.formatMoney(data.full);
            const save = document.createElement('span');
            save.className = `vip-bar-pricing__save${pillTemplate ? ' vip-bar-pricing__save--pill' : ''}`;
            save.textContent = savings;
            nodes.push(full, save);
          }
          element.replaceChildren(...nodes);
        });
      }

      // "$30.00 each" under the bundle name, for bundles with 2 or more items.
      renderBundleEach(bar, data, shown) {
        const host = bar.querySelector('.kaching-bundles__bar-content-left');
        if (!host) return;
        let line = host.querySelector(':scope > .vip-bar-each');
        const quantity = Math.round(data.full / this.unitPrice());
        if (!this.dataset.bundleEach || !(quantity > 1)) {
          line?.remove();
          return;
        }
        const text = this.dataset.bundleEach.replace('[price]', this.formatMoney(Math.round(shown / quantity)));
        if (line?.textContent === text) return;
        if (!line) {
          line = document.createElement('span');
          line.className = 'vip-bar-each';
          host.append(line);
        }
        line.textContent = text;
      }

      // The line under the toggle. Regular: what Member Price would save on the selected bundle.
      renderNote() {
        const note = this.toggleEl?.querySelector('[data-vip-note]');
        if (!note) return;
        let text = this.dataset.noteMember;
        if (!this.isMember) {
          const selected = this.readBar(this.kachingBlock?.querySelector('.kaching-bundles__bar--selected'));
          const price = selected?.price ?? this.kachingPrice ?? this.getThemeBundlePrice();
          const full = selected?.full ?? price;
          text =
            full < this.threshold
              ? this.dataset.noteRegularPercent
              : this.dataset.noteRegularAmount.replace('[savings]', this.formatMoney(full - this.memberPriceOf(price)));
        }
        if (note.dataset.text === text) return;
        note.dataset.text = text;
        // *words* between asterisks are highlighted in gold.
        note.replaceChildren(
          ...text.split(/\*([^*]+)\*/).map((part, index) => {
            if (index % 2 === 0) return document.createTextNode(part);
            const highlight = document.createElement('strong');
            highlight.className = 'vip-toggle__highlight';
            highlight.textContent = part;
            return highlight;
          })
        );
      }

      // Kaching's documented pricing() API: the selected deal's price before its discount, in dollars.
      getFullPrice() {
        try {
          const amount = this.kachingBlock?.pricing?.()?.fullPrice?.amount;
          return typeof amount === 'number' ? Math.round(amount * 100) : null;
        } catch {
          return null;
        }
      }

      toCents(amount) {
        return Number.isInteger(amount) ? amount : Math.round(amount * 100);
      }

      getQuantityInput() {
        // Not this.form.id: the form has an input named "id", which shadows that property.
        const id = this.dataset.formId;
        return (
          document.querySelector(`input[name="quantity"][form="${id}"]:checked`) ||
          document.querySelector(`input[name="quantity"][form="${id}"]:not([type="radio"])`) ||
          this.form.querySelector('input[name="quantity"]')
        );
      }

      // Used when Kaching isn't on the page. Mirrors the price math in snippets/quantity-breaks.liquid.
      getThemeBundlePrice() {
        const variantId = this.form.querySelector('[name="id"]')?.value;
        const unitPrice = this.variantPrices[variantId] ?? Object.values(this.variantPrices)[0];
        const quantityInput = this.getQuantityInput();
        const quantity = parseInt(quantityInput?.value, 10) || 1;

        let percentageLeft = 1;
        let fixedDiscount = 0;
        if (quantityInput?.type === 'radio') {
          const label = document.querySelector(`#quantity-breaks-${this.sectionId} label[for="${quantityInput.id}"]`);
          percentageLeft = parseFloat(label?.dataset.percentageLeft) || 1;
          fixedDiscount = parseFloat(label?.dataset.fixedDiscount) || 0;
        }

        return Math.max(0, Math.round(unitPrice * quantity * percentageLeft - fixedDiscount));
      }

      update() {
        // Kaching's documented pricing() gives the selected deal's price in dollars.
        try {
          const amount = this.kachingBlock?.pricing?.()?.discountedPrice?.amount;
          if (typeof amount === 'number') this.kachingPrice = Math.round(amount * 100);
        } catch {
          // Older Kaching versions: keep the price from the variants-changed event.
        }
        const regular = this.kachingPrice ?? this.getThemeBundlePrice();
        // Shopify rounds the percentage discount amount down to the cent.
        const member = regular - Math.floor((regular * this.discount) / 100);
        const deal = this.dealPriceOf(regular);

        this.setText('[data-vip-price]', this.formatMoney(deal ?? member));
        this.renderDeal(regular, deal);
        // The VIP price is crossed out against the full price before bundle savings
        // (e.g. $104.97 for "Buy 2, get 1 free"), not the bundle price.
        this.setText('[data-vip-compare]', this.formatMoney(Math.max(regular, this.getFullPrice() ?? 0)));
        this.setText('[data-regular-price]', this.formatMoney(regular));
        this.regularPriceText = this.formatMoney(regular);
        this.placePerks();

        // The dollar amount only shows from the threshold up (default $100): below it a
        // percentage reads as the bigger deal, above it the dollar amount does.
        const amount = this.querySelector('[data-vip-savings-amount]');
        if (amount) {
          amount.textContent = amount.dataset.template.replace('[savings]', this.formatMoney(regular - member));
          amount.hidden = regular < Number(amount.dataset.threshold || 0);
        }

        const buttonPrice = this.querySelector('[data-vip-button-price]');
        if (buttonPrice) buttonPrice.textContent = `\u00b7 ${this.formatMoney(member)}`;

        const savings = this.querySelector('[data-vip-savings]');
        if (savings) savings.textContent = savings.dataset.template.replace('[savings]', this.formatMoney(regular - member));

        const disclosure = this.querySelector('[data-vip-disclosure]');
        if (disclosure) {
          this.renderDisclosure(disclosure, member);
          this.fitDisclosure();
        }

        this.updateStickyPrice(regular, deal ?? member, deal !== null);
        this.renderClubRow(regular, member);
        this.renderBundlePricing();
        this.renderNote();

        if (this.isExistingMember) return;
        this.classList.toggle('vip-membership--member', this.isMember);
        this.themeButtons.forEach((button) => button.classList.toggle('vip-membership-hidden', this.isMember));
        this.relabelThemeButton();
        // The billing line is only measurable once the member details are visible.
        this.fitDisclosure();
      }

      renderClubRow(regular, member) {
        const row = this.clubRow;
        if (!row) return;
        const buttons = this.submitButton?.closest('.product-form__buttons') || this.form?.querySelector('.product-form__buttons');
        if (buttons && buttons.previousElementSibling !== row) buttons.before(row);
        row.hidden = this.isExistingMember || this.isMember;
        row.querySelector('[data-vip-club-box]').checked = false;
        // Crossed out: the full price before bundle savings (e.g. 5 x $44.99 for
        // "Buy 3, get 2 free"), so "You save" is the whole saving with VIP, the same
        // amount as the note under the toggle.
        const full = Math.max(regular, this.getFullPrice() ?? 0);
        row.querySelector('[data-vip-club-regular]').textContent = this.formatMoney(full);
        row.querySelector('[data-vip-club-member]').textContent = this.formatMoney(member);
        const save = row.querySelector('[data-vip-club-save]');
        if (save) save.textContent = (this.dataset.saveAmount || 'You save [savings]').replace('[savings]', this.formatMoney(full - member));
        if (!row.hidden) this.fitClubTerms();
      }

      fitClubTerms() {
        const line = this.clubRow?.querySelector('.vip-club-row__terms');
        if (!line || !line.clientWidth || getComputedStyle(line).whiteSpace !== 'nowrap') return;
        line.style.fontSize = '';
        line.style.whiteSpace = '';
        let size = parseFloat(getComputedStyle(line).fontSize);
        while (line.scrollWidth > line.clientWidth && size > 10) {
          size -= 0.5;
          line.style.fontSize = `${size}px`;
        }
        // Still too long (narrow phones, longer languages): wrap at the normal size.
        if (line.scrollWidth > line.clientWidth) {
          line.style.fontSize = '';
          line.style.whiteSpace = 'normal';
        }
        if (!this.fitClubOnResize) {
          this.fitClubOnResize = true;
          window.addEventListener('resize', () => this.fitClubTerms());
        }
      }

      // The sticky bar shows the price of what is selected (bundle, VIP or regular)
      // instead of the theme's single-item price.
      updateStickyPrice(regular, member, isDeal = false) {
        const container = this.stickyAtc?.querySelector(`#sticky-atc-separate-price-${this.sectionId}`);
        if (!container) return;

        let price = container.querySelector('.vip-sticky-price');
        if (!price) {
          price = document.createElement('span');
          price.className = 'vip-sticky-price price-item';
          container.prepend(price);
        }
        // The theme's own price is display: flex, so it is hidden with a class rather than [hidden].
        container.classList.add('vip-sticky-price-active');

        const showMember = this.isExistingMember || this.isMember;
        const nodes = [document.createTextNode(this.formatMoney(showMember ? member : regular))];
        if (showMember) {
          const compare = document.createElement('s');
          compare.textContent = this.formatMoney(Math.max(regular, this.getFullPrice() ?? 0));
          const tag = document.createElement('span');
          tag.className = 'vip-sticky-price__tag';
          tag.textContent =
            (isDeal && this.dataset.dealStickyTag) || this.dataset.stickyTag || `VIP ${this.dataset.discount}% off`;
          nodes.push(compare, tag);
        }
        price.replaceChildren(...nodes);
      }

      // "[link]" in the billing line becomes a button that opens the VIP terms popup.
      renderDisclosure(element, member) {
        const text = element.dataset.template
          .replace('[today]', this.formatMoney(member))
          .replace('[recurring]', element.dataset.recurring)
          .replace('[date]', this.firstBillingDate());
        const [before, ...rest] = text.split('[link]');
        // *words* between asterisks are bold (the price and "billed automatically").
        const textNodes = (part) =>
          part.split(/\*([^*]+)\*/).map((piece, index) => {
            if (index % 2 === 0) return document.createTextNode(piece);
            const strong = document.createElement('strong');
            strong.textContent = piece;
            return strong;
          });
        const nodes = textNodes(before);
        if (rest.length && this.terms) {
          const link = document.createElement('button');
          link.type = 'button';
          link.className = 'vip-membership__terms-link';
          link.textContent = element.dataset.linkLabel || 'VIP';
          link.addEventListener('click', () => this.terms.showModal());
          // Keep punctuation right after the link (e.g. ":") on the same line as it.
          const after = rest.join('[link]');
          const punctuation = after.match(/^[:;,.]/)?.[0] || '';
          const linkGroup = document.createElement('span');
          linkGroup.style.whiteSpace = 'nowrap';
          linkGroup.append(link, punctuation);
          nodes.push(linkGroup, ...textNodes(after.slice(punctuation.length)));
        } else if (rest.length) {
          nodes.push(...textNodes((element.dataset.linkLabel || 'VIP') + rest.join('[link]')));
        }
        element.replaceChildren(...nodes);
      }

      // Toggle layout: the billing line stays on one line, shrinking its text a
      // little on narrow screens (never below 10.5px), then wraps.
      fitDisclosure() {
        const line = this.querySelector('[data-vip-disclosure]');
        if (this.dataset.layout !== 'toggle' || !line || !line.clientWidth) return;
        if (line.classList.contains('vip-membership__disclosure--paragraph')) return;
        line.style.fontSize = '';
        let size = parseFloat(getComputedStyle(line).fontSize);
        line.style.whiteSpace = '';
        while (line.scrollWidth > line.clientWidth && size > 9) {
          size -= 0.5;
          line.style.fontSize = `${size}px`;
        }
        // Still too long at the smallest size (longer languages): wrap rather than cut it off.
        if (line.scrollWidth > line.clientWidth) line.style.whiteSpace = 'normal';
        if (!this.fitOnResize) {
          this.fitOnResize = true;
          window.addEventListener('resize', () => this.fitDisclosure());
        }
      }

      // Pages in another language: the theme's own Add to cart (shown for Regular Price)
      // uses the store's language, so it takes the block's label instead. A sold-out
      // (disabled) button keeps the theme's text.
      relabelThemeButton() {
        const text = this.submitButton?.querySelector('.main-atc__label__text');
        if (!text || this.submitButton.disabled) return;
        let label = this.dataset.regularButtonLabel;
        if (this.dataset.regularButtonPrice === 'true' && this.regularPriceText) {
          // Remember the theme's own wording once, before the price is added to it.
          this.themeButtonBase ??= label || text.textContent.trim();
          label = `${this.themeButtonBase} \u00b7 ${this.regularPriceText}`;
        }
        if (!label || text.textContent.trim() === label) return;
        text.textContent = label;
      }

      setText(selector, text) {
        const element = this.querySelector(selector);
        if (element) element.textContent = text;
      }

      firstBillingDate() {
        const date = new Date();
        date.setDate(date.getDate() + 14);
        return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
      }

      // Adds the membership first, then clicks the theme's (hidden) Add to cart
      // button so the theme and Kaching add the selected bundle as usual.
      async submit() {
        if (!this.submitButton) return;
        this.setLoading(true);

        try {
          await this.ensureMembershipInCart();
        } catch (error) {
          this.showError(error.message);
          this.setLoading(false);
          return;
        }

        this.submitButton.click();
        setTimeout(() => this.setLoading(false), 1500);
      }

      async ensureMembershipInCart() {
        if (await this.cartHasMembership()) return;

        const response = await fetch(`${this.routesRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.membershipVariantId, quantity: 1, selling_plan: this.sellingPlanId }],
          }),
        });
        const data = await this.readJson(response);
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add the VIP Membership.');

        // Cart interceptors (e.g. Kaching's) can hand back an empty or reused
        // response body, so confirm against the cart itself.
        if (!(await this.cartHasMembership())) throw new Error('Could not add the VIP Membership. Please try again.');
      }

      // Shopify's own root (handles market/language prefixes). Not window.routes:
      // on this store something overwrites routes.cart_add_url with a relative "cart".
      get routesRoot() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }

      async cartHasMembership() {
        const cart = await this.readJson(await fetch(`${this.routesRoot}cart.js`, { headers: { Accept: 'application/json' } }));
        return (cart.items || []).some((item) => item.variant_id === this.membershipVariantId);
      }

      async readJson(response) {
        const text = await response.text();
        try {
          return text ? JSON.parse(text) : {};
        } catch {
          return {};
        }
      }

      setLoading(loading) {
        this.vipButton.toggleAttribute('disabled', loading);
        this.vipButton.classList.toggle('loading', loading);
        if (loading) this.errorEl.hidden = true;
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      formatMoney(cents) {
        const format = (amount, decimals, thousands = ',', decimal = '.') => {
          const [whole, fraction] = (amount / 100).toFixed(decimals).split('.');
          const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? withThousands + decimal + fraction : withThousands;
        };

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, type) => {
          switch (type) {
            case 'amount_no_decimals':
              return format(cents, 0);
            case 'amount_with_comma_separator':
              return format(cents, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return format(cents, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return format(cents, 2, "'", '.');
            default:
              return format(cents, 2);
          }
        });
      }
    }
  );
}
